"use client";

import * as React from "react";

import { cn } from "@/lib/utils";
import { buildImageProxyUrl, getDisplayImageUrl, isOurMediaUrl } from "@/lib/utils/image-proxy";
import { getPublicMediaUrl } from "@/lib/utils/media-url";

const STORAGE_KEY = "techxeed_failed_avatar_urls";
const MAX_STORED = 30;
const TECHXEED_MEDIA_HOSTS = [
	"d28v02rpv54mir.cloudfront.net",
	"techxeed-prod-data.s3.us-east-1.amazonaws.com",
];

function loadFailedSet(): Set<string> {
	if (typeof window === "undefined") return new Set();
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return new Set();
		const arr = JSON.parse(raw) as string[];
		return new Set(Array.isArray(arr) ? arr.slice(-MAX_STORED) : []);
	} catch {
		return new Set();
	}
}

function saveFailedSet(set: Set<string>) {
	try {
		const arr = [...set];
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(arr.slice(-MAX_STORED)));
	} catch {
		// ignore
	}
}

const failedAvatarUrls = typeof window !== "undefined" ? loadFailedSet() : new Set<string>();
function normalizeForFailedAvatarStore(url: string): string {
	if (!isOurMediaUrl(url)) return url;
	try {
		const parsed = new URL(url);
		parsed.search = "";
		return parsed.toString();
	} catch {
		return url.split("?")[0] ?? url;
	}
}

function addFailedAvatarUrl(url: string) {
	failedAvatarUrls.add(normalizeForFailedAvatarStore(url));
	saveFailedSet(failedAvatarUrls);
}

function isKnownMissingAvatarUrl(url: string): boolean {
	const trimmed = url.trim();
	if (!trimmed) return true;
	return (
		trimmed.includes("default.jpeg") ||
		trimmed.includes(
			"admin_assets/136c0d35-5edf-417c-ad3f-de87537afa8b-default.jpeg",
		)
	);
}

function isRetryableTechxeedMediaUrl(url: string): boolean {
	return TECHXEED_MEDIA_HOSTS.some((host) => url.includes(host));
}

const Avatar = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			className={cn("relative flex shrink-0 overflow-hidden rounded-full", className)}
			{...props}
		/>
	),
);
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef<
	HTMLImageElement,
	Omit<React.ComponentPropsWithoutRef<"img">, "src"> & {
		src?: string | null;
		loading?: "eager" | "lazy";
	}
>(({ className, onContextMenu, onDragStart, onError, onLoad, src, alt = "", loading = "lazy", ...props }, ref): React.ReactElement | null => {
	const srcString = typeof src === "string" ? src : "";
	const primarySrc =
		srcString && !isKnownMissingAvatarUrl(srcString)
			? getDisplayImageUrl(srcString) ?? srcString
			: "";
	const [activeSrc, setActiveSrc] = React.useState(primarySrc);

	React.useLayoutEffect(() => {
		setActiveSrc(primarySrc);
	}, [primarySrc]);

	const effectiveSrc = activeSrc || primarySrc;

	const skipFromStore =
		!!effectiveSrc &&
		failedAvatarUrls.has(normalizeForFailedAvatarStore(effectiveSrc)) &&
		!isRetryableTechxeedMediaUrl(effectiveSrc);
	const [localFailed, setLocalFailed] = React.useState(false);
	const skipLoad = skipFromStore || localFailed;
	const [loaded, setLoaded] = React.useState(false);
	const innerRef = React.useRef<HTMLImageElement | null>(null);

	const setImgRef = React.useCallback(
		(node: HTMLImageElement | null) => {
			innerRef.current = node;
			if (typeof ref === "function") {
				ref(node);
			} else if (ref) {
				(ref as React.MutableRefObject<HTMLImageElement | null>).current = node;
			}
		},
		[ref],
	);

	React.useLayoutEffect(() => {
		setLocalFailed(false);
		const el = innerRef.current;
		if (el?.complete && el.naturalWidth > 0) {
			setLoaded(true);
		} else {
			setLoaded(false);
		}
	}, [effectiveSrc]);

	const handleError = React.useCallback(
		(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
			const url = (e.target as HTMLImageElement)?.src;
			if (
				srcString &&
				isRetryableTechxeedMediaUrl(srcString) &&
				url &&
				!url.includes("/api/image-proxy")
			) {
				const canonical = getPublicMediaUrl(srcString) ?? srcString;
				setActiveSrc(buildImageProxyUrl(canonical));
				setLoaded(false);
				return;
			}
			if (url && !isRetryableTechxeedMediaUrl(url)) addFailedAvatarUrl(url);
			setLocalFailed(true);
			onError?.(e);
		},
		[onError, srcString],
	);

	const handleLoad = React.useCallback(
		(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
			setLoaded(true);
			onLoad?.(e);
		},
		[onLoad],
	);

	const preventImageDownload = React.useCallback(
		(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
			e.preventDefault();
			if (e.type === "contextmenu") {
				onContextMenu?.(e as React.MouseEvent<HTMLImageElement>);
			} else if (e.type === "dragstart") {
				onDragStart?.(e as React.DragEvent<HTMLImageElement>);
			}
		},
		[onContextMenu, onDragStart],
	);

	if (!srcString || isKnownMissingAvatarUrl(srcString)) {
		return null;
	}

	if (skipLoad) {
		return null;
	}

	return (
		<img
			ref={setImgRef}
			src={effectiveSrc}
			alt={alt}
			className={cn(
				"absolute inset-0 z-10 size-full select-none object-cover transition-opacity duration-150 ease-out [-webkit-user-drag:none] [user-drag:none] motion-reduce:transition-none",
				loaded ? "opacity-100" : "opacity-0",
				className,
			)}
			draggable={false}
			onContextMenu={preventImageDownload}
			onDragStart={preventImageDownload}
			onError={handleError}
			onLoad={handleLoad}
			loading={loading}
			decoding="async"
			{...props}
		/>
	);
});
AvatarImage.displayName = "AvatarImage";

type AvatarFallbackProps = React.HTMLAttributes<HTMLDivElement> & {
	/** @deprecated ignored; kept for API compatibility with Radix */
	delayMs?: number;
};

const AvatarFallback = React.forwardRef<HTMLDivElement, AvatarFallbackProps>(
	({ className, delayMs: _delayMs, style, ...props }, ref) => (
		<div
			ref={ref}
			className={cn(
				"absolute inset-0 z-0 flex h-full w-full items-center justify-center rounded-full bg-[#07A8BC] font-semibold text-white",
				className,
			)}
			{...props}
			style={style}
		/>
	),
);
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarImage, AvatarFallback };
